import { Button } from '@/components/Button/Button';
import { Input } from '@/components/Input/Input';
import { ListSection } from '@/components/List/ListSection';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useSafeAreaPadding } from '@/hooks/useSafeAreaPadding';
import type { FormOption } from '@/screens/ReportCreation/reportCreation.types';
import { Ionicons } from '@expo/vector-icons';
import { useMemo, useState } from 'react';
import { Pressable, ScrollView } from 'react-native';
import { Sheet, SizableText, useTheme, YStack } from 'tamagui';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';

interface AutocompleteFieldProps {
  label?: string;
  placeholder?: string;
  value: string;
  options: FormOption[];
  onValueChange: (value: string) => void;
  required?: boolean;
  allowCustomValue?: boolean;
  helperText?: string;
}

const filterOptions = (query: string, options: FormOption[]): FormOption[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return options;
  }

  return options.filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery),
  );
};

export const AutocompleteField = ({
  label,
  placeholder,
  value,
  onValueChange,
  options,
  required = false,
  allowCustomValue = false,
  helperText,
}: AutocompleteFieldProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const padding = useSafeAreaPadding();
  const theme = useTheme();

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const normalizedQuery = searchQuery.trim();
  const shouldShowHelperText =
    Boolean(helperText) && normalizedQuery.length === 0;
  const filteredOptions = useMemo(
    () => filterOptions(searchQuery, options),
    [searchQuery, options],
  );

  const hasValueMatch = options.some(
    (option) => option.value === normalizedQuery,
  );
  const hasLabelMatch = options.some(
    (option) =>
      option.label.trim().toLowerCase() === normalizedQuery.toLowerCase(),
  );

  const shouldShowCustomOption =
    allowCustomValue &&
    normalizedQuery.length > 0 &&
    !hasValueMatch &&
    !hasLabelMatch;

  const customOptionLabel = shouldShowCustomOption
    ? t('reportCreation.common.useCustomValue', { value: normalizedQuery })
    : undefined;

  const displayOptions = shouldShowCustomOption
    ? [
        { value: normalizedQuery, label: customOptionLabel ?? normalizedQuery },
      ].concat(filteredOptions)
    : filteredOptions;

  const handleSelect = (selectedValue: string): void => {
    onValueChange(selectedValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleOpen = (): void => {
    setIsOpen(true);
  };

  const handleClose = (openValue: boolean): void => {
    setIsOpen(openValue);
    if (!openValue) {
      setSearchQuery('');
    }
  };

  const displayValue = selectedOption?.label ?? value;

  return (
    <YStack gap="$1">
      {label && <FieldTitle label={label} required={required} />}
      <Pressable onPress={handleOpen}>
        <YStack pointerEvents="none">
          <Input
            value={displayValue}
            placeholder={placeholder}
            editable={false}
          />
        </YStack>
      </Pressable>

      <Sheet
        open={isOpen}
        onOpenChange={handleClose}
        snapPoints={[100]}
        snapPointsMode="percent"
        modal
        disableDrag
      >
        <Sheet.Frame
          paddingTop={padding.topPadding}
          paddingBottom={padding.bottomPadding}
          borderRadius={0}
          flex={1}
        >
          <YStack flex={1} backgroundColor="$background">
            <YStack paddingHorizontal="$true">
              <Button
                variant="ghost"
                icon={
                  <Ionicons name="close" size={24} color={theme.color.val} />
                }
                onPress={() => handleClose(false)}
                maxWidth={30}
                padding={0}
              />
            </YStack>

            <YStack padding="$true" gap="$2">
              <Input
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder={placeholder}
                autoCapitalize="none"
              />
              {shouldShowHelperText && (
                <SizableText size="$2" color="$secondary75">
                  {helperText}
                </SizableText>
              )}
            </YStack>

            <ScrollView
              keyboardShouldPersistTaps="always"
              nestedScrollEnabled
              style={{ flex: 1 }}
              contentContainerStyle={{
                flexGrow: 1,
              }}
            >
              <YStack gap="$4" paddingHorizontal="$true">
                {displayOptions.length === 0 ? (
                  <SizableText color="$secondary75">
                    {t('reportCreation.common.noResults')}
                  </SizableText>
                ) : (
                  <ListSection
                    title=""
                    items={displayOptions.map((option) => ({
                      id: option.value,
                      title: option.label,
                      onPress: () => handleSelect(option.value),
                      icon:
                        option.value === value ? (
                          <Ionicons name="checkmark" size={16} />
                        ) : undefined,
                    }))}
                  />
                )}
              </YStack>
            </ScrollView>
          </YStack>
        </Sheet.Frame>
        <Sheet.Overlay backgroundColor="$shadow" />
      </Sheet>
    </YStack>
  );
};
